import React, { useState, useCallback } from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  CircleDollarSign,
  Percent,
  Coins,
  Calculator,
  ArrowRightLeft,
} from "lucide-react";

const PricingSection = React.memo(
  ({
    onPriceChange,
    initialPrice,
    initialDiscountedPrice,
    onValidationChange,
  }) => {
    const [priceError, setPriceError] = useState("");
    const [originalPrice, setOriginalPrice] = useState(initialPrice);
    const [discountedPrice, setDiscountedPrice] = useState(
      initialDiscountedPrice
    );
    const [isFocused, setIsFocused] = useState({
      original: false,
      discounted: false,
    });

    // New state for dual input mode
    const [inputMode, setInputMode] = useState("sales"); // 'sales' or 'percentage'
    const [discountPercentage, setDiscountPercentage] = useState("");
    const [lastEnteredSalesPrice, setLastEnteredSalesPrice] = useState(
      initialDiscountedPrice
    );
    const [, setLastEnteredPercentage] = useState("");

    const formatPrice = (value) => {
      if (!value) return "";
      // Ensure two decimal places
      return Number(value).toFixed(2);
    };

    // Helper function to ensure discounted price always has 2 decimal places
    const formatDiscountedPrice = (value) => {
      if (!value) return "";
      // Always ensure two decimal places for discounted price
      return Number(value).toFixed(2);
    };


    const calculateDiscount = (originalPrice, discountedPrice) => {
      if (!originalPrice || !discountedPrice)
        return { percentage: "0.0", value: "0.00" };
      const discount = originalPrice - discountedPrice;
      const percentage = ((discount / originalPrice) * 100).toFixed(1);
      return {
        percentage: percentage,
        value: discount.toFixed(2),
      };
    };

    const calculateSalesPriceFromPercentage = (originalPrice, percentage) => {
      if (!originalPrice || !percentage) return "0.00";
      const discount = (originalPrice * percentage) / 100;
      const result = originalPrice - discount;
      return result.toFixed(2);
    };

    const calculatePercentageFromSalesPrice = (originalPrice, salesPrice) => {
      if (!originalPrice || !salesPrice) return 0;
      const discount = originalPrice - salesPrice;
      return ((discount / originalPrice) * 100).toFixed(1);
    };

    const discount = calculateDiscount(
      Number(originalPrice),
      Number(discountedPrice)
    );

    const validatePrices = useCallback(
      (origPrice, discPrice) => {
        if (Number(discPrice) > Number(origPrice)) {
          setPriceError(
            "Discounted price cannot be greater than original price"
          );
          onValidationChange?.(false);
          return false;
        }
        if (Number(discPrice) < 0) {
          setPriceError("Discounted price cannot be negative");
          onValidationChange?.(false);
          return false;
        }
        setPriceError("");
        onValidationChange?.(true);
        return true;
      },
      [onValidationChange]
    );

    const handlePriceChange = useCallback(
      (e, type) => {
        // Prevent non-numeric input
        const value = e.target.value.replace(/[^0-9.]/g, "");

        // Ensure only one decimal point
        const decimalCount = (value.match(/\./g) || []).length;
        if (decimalCount > 1) return;

        // Limit to 2 decimal places
        if (value.includes(".") && value.split(".")[1]?.length > 2) return;

        const numValue = Number(value);

        if (type === "original") {
          setOriginalPrice(value);
          onPriceChange("price", formatPrice(value));

          // If we're in percentage mode, recalculate the sales price
          if (inputMode === "percentage" && discountPercentage) {
            const newSalesPrice = calculateSalesPriceFromPercentage(
              numValue,
              Number(discountPercentage)
            );
            setDiscountedPrice(newSalesPrice);
            onPriceChange(
              "discounted_price",
              formatDiscountedPrice(newSalesPrice)
            );
            validatePrices(value, newSalesPrice);
          } else {
            validatePrices(value, discountedPrice);
          }
        } else if (type === "discounted") {
          setDiscountedPrice(value);
          onPriceChange("discounted_price", formatDiscountedPrice(value));

          // Store the last entered sales price
          setLastEnteredSalesPrice(value);

          // If we're in percentage mode, update the percentage display
          if (inputMode === "percentage") {
            const newPercentage = calculatePercentageFromSalesPrice(
              Number(originalPrice),
              numValue
            );
            setDiscountPercentage(newPercentage);
            setLastEnteredPercentage(newPercentage);
          }

          validatePrices(originalPrice, value);
        }
      },
      [
        discountedPrice,
        originalPrice,
        onPriceChange,
        validatePrices,
        inputMode,
        discountPercentage,
      ]
    );

    const handlePercentageChange = useCallback(
      (e) => {
        // Prevent non-numeric input
        const value = e.target.value.replace(/[^0-9.]/g, "");

        // Ensure only one decimal point
        const decimalCount = (value.match(/\./g) || []).length;
        if (decimalCount > 1) return;

        // Limit to 1 decimal place
        if (value.includes(".") && value.split(".")[1]?.length > 1) return;

        // Limit percentage to 0-100
        if (Number(value) > 100) return;

        setDiscountPercentage(value);
        setLastEnteredPercentage(value);

        if (originalPrice && value) {
          const newSalesPrice = calculateSalesPriceFromPercentage(
            Number(originalPrice),
            Number(value)
          );
          setDiscountedPrice(newSalesPrice);
          onPriceChange(
            "discounted_price",
            formatDiscountedPrice(newSalesPrice)
          );
          validatePrices(originalPrice, newSalesPrice);
        }

        if (!value) {
          setDiscountedPrice("");
          onPriceChange("discounted_price", "");
        }
      },
      [originalPrice, onPriceChange, validatePrices]
    );

    const handleInputModeChange = useCallback(
      (event, newMode) => {
        if (newMode === null) return;

        if (newMode === "percentage" && inputMode === "sales") {
          if (originalPrice && discountedPrice) {
            const percentage = calculatePercentageFromSalesPrice(
              Number(originalPrice),
              Number(discountedPrice)
            );
            setDiscountPercentage(percentage);
            setLastEnteredPercentage(percentage);
          }
        } else if (newMode === "sales" && inputMode === "percentage") {
          if (lastEnteredSalesPrice) {
            setDiscountedPrice(lastEnteredSalesPrice);
            onPriceChange(
              "discounted_price",
              formatDiscountedPrice(lastEnteredSalesPrice)
            );
            validatePrices(originalPrice, lastEnteredSalesPrice);
          }
        }

        setInputMode(newMode);
      },
      [
        inputMode,
        originalPrice,
        discountedPrice,
        lastEnteredSalesPrice,
        onPriceChange,
        validatePrices,
      ]
    );

    // Validate prices on initial render
    React.useEffect(() => {
      validatePrices(initialPrice, initialDiscountedPrice);
    }, [initialPrice, initialDiscountedPrice, validatePrices]);

    // Ensure initial values are properly formatted when component mounts
    React.useEffect(() => {
      if (initialPrice) {
        onPriceChange("price", formatPrice(initialPrice));
      }
      if (initialDiscountedPrice) {
        onPriceChange(
          "discounted_price",
          formatDiscountedPrice(initialDiscountedPrice)
        );
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Keyboard shortcuts for quick mode switching
    React.useEffect(() => {
      const handleKeyPress = (e) => {
        // Ctrl/Cmd + S for Sales Price mode
        if ((e.ctrlKey || e.metaKey) && e.key === "s") {
          e.preventDefault();
          if (inputMode !== "sales") {
            handleInputModeChange(null, "sales");
          }
        }
        // Ctrl/Cmd + P for Percentage mode
        if ((e.ctrlKey || e.metaKey) && e.key === "p") {
          e.preventDefault();
          if (inputMode !== "percentage") {
            handleInputModeChange(null, "percentage");
          }
        }
      };

      document.addEventListener("keydown", handleKeyPress);
      return () => document.removeEventListener("keydown", handleKeyPress);
    }, [inputMode, handleInputModeChange]);

    return (
      <motion.div
        className="p-8 bg-white rounded-2xl border border-gray-100 shadow-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <h3 className="flex gap-2 items-center mb-6 text-lg font-semibold text-gray-900">
          <Coins className="w-6 h-6 text-[#FF6C6C]" />
          Pricing Details
        </h3>

        <div className="space-y-6">
          {/* Input Mode Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col gap-3"
          >
            <div className="flex gap-2 items-center">
              <Calculator className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                Input Mode
              </span>
              <span className="text-xs text-gray-400">
                (Ctrl+S: Sales, Ctrl+P: Percentage)
              </span>
            </div>

            <ToggleButtonGroup
              value={inputMode}
              exclusive
              onChange={handleInputModeChange}
              className="p-1 bg-gray-50 rounded-xl"
              sx={{
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 500,
                  transition: "all 0.2s ease",
                  "&.Mui-selected": {
                    backgroundColor: "#FF6C6C",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#FF8F8F",
                    },
                  },
                  "&:not(.Mui-selected)": {
                    backgroundColor: "transparent",
                    color: "#6B7280",
                    "&:hover": {
                      backgroundColor: "rgba(255, 108, 108, 0.1)",
                      color: "#FF6C6C",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="sales" className="px-6 py-2">
                <div className="flex gap-2 items-center">
                  <Coins className="w-4 h-4" />
                  <span>Sales Price</span>
                </div>
              </ToggleButton>
              <ToggleButton value="percentage" className="px-6 py-2">
                <div className="flex gap-2 items-center">
                  <Percent className="w-4 h-4" />
                  <span>Discount %</span>
                </div>
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Quick Discount Suggestions - Only show in percentage mode */}
            <AnimatePresence>
              {inputMode === "percentage" && originalPrice && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-2"
                >
                  <div className="flex gap-2 items-center mb-2">
                    <span className="text-xs text-gray-500">
                      Quick suggestions:
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[10, 15, 20, 25, 30, 40, 50].map((percentage) => (
                      <motion.button
                        key={percentage}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setDiscountPercentage(percentage.toString());
                          setLastEnteredPercentage(percentage.toString());
                          const newSalesPrice =
                            calculateSalesPriceFromPercentage(
                              Number(originalPrice),
                              percentage
                            );
                          setDiscountedPrice(newSalesPrice);
                          onPriceChange(
                            "discounted_price",
                            formatDiscountedPrice(newSalesPrice)
                          );
                          validatePrices(originalPrice, newSalesPrice);
                        }}
                        className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-[#FF6C6C]/10 hover:text-[#FF6C6C] transition-all duration-200"
                      >
                        {percentage}%
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Original Price Input */}
            <motion.div
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="relative"
            >
              <div
                className={`
              relative rounded-2xl border transition-all duration-200
              ${
                priceError
                  ? "border-red-300"
                  : isFocused.original
                  ? "border-[#FF6C6C]"
                  : "border-gray-200"
              }
              ${isFocused.original ? "shadow-lg shadow-[#FF6C6C]/10" : ""}
            `}
              >
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  <Coins
                    className={`w-5 h-5 ${
                      isFocused.original ? "text-[#FF6C6C]" : "text-gray-400"
                    }`}
                  />
                </div>
                <input
                  type="text"
                  value={originalPrice}
                  onChange={(e) => handlePriceChange(e, "original")}
                  onFocus={() =>
                    setIsFocused((prev) => ({ ...prev, original: true }))
                  }
                  onBlur={() =>
                    setIsFocused((prev) => ({ ...prev, original: false }))
                  }
                  pattern="^\d*\.?\d{0,2}$"
                  inputMode="decimal"
                  placeholder="0.00"
                  className="py-4 pr-16 pl-12 w-full text-lg bg-transparent rounded-2xl outline-none"
                />
                <motion.div
                  className="flex absolute right-4 top-1/2 gap-1 items-center -translate-y-1/2"
                  animate={{ opacity: isFocused.original ? 1 : 0.5 }}
                >
                  <span className="px-2 py-1 text-xs text-gray-400 bg-gray-50 rounded-md">
                    .00
                  </span>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isFocused.original ? 1 : 0 }}
                    className="absolute right-0 -bottom-10 text-xs text-gray-500 whitespace-nowrap"
                  >
                    Two decimal places only
                  </motion.div>
                </motion.div>
              </div>
              <motion.label
                className={`absolute -top-2.5 left-4 px-2 text-sm bg-white
                ${isFocused.original ? "text-[#FF6C6C]" : "text-gray-500"}`}
              >
                Original Price
              </motion.label>
            </motion.div>

            {/* Sales Price or Discount Percentage Input */}
            <motion.div
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="relative"
            >
              {/* Mode Indicator Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`absolute -top-3 right-4 px-3 py-1 text-xs font-medium rounded-full z-10 ${
                  inputMode === "sales"
                    ? "bg-blue-100 text-blue-700 border border-blue-200"
                    : "bg-purple-100 text-purple-700 border border-purple-200"
                }`}
              >
                {inputMode === "sales" ? "💰 Sales Price" : "📊 Discount %"}
              </motion.div>
              <div
                className={`
              relative rounded-2xl border transition-all duration-200
              ${
                priceError
                  ? "border-red-300"
                  : isFocused.discounted
                  ? "border-[#FF6C6C]"
                  : "border-gray-200"
              }
              ${isFocused.discounted ? "shadow-lg shadow-[#FF6C6C]/10" : ""}
              ${Number(originalPrice) <= 0 ? "opacity-50" : ""}
            `}
              >
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                  {inputMode === "sales" ? (
                    <Coins
                      className={`w-5 h-5 ${
                        isFocused.discounted
                          ? "text-[#FF6C6C]"
                          : "text-gray-400"
                      }`}
                    />
                  ) : (
                    <Percent
                      className={`w-5 h-5 ${
                        isFocused.discounted
                          ? "text-[#FF6C6C]"
                          : "text-gray-400"
                      }`}
                    />
                  )}
                </div>

                {inputMode === "sales" ? (
                  <input
                    type="text"
                    value={discountedPrice}
                    onChange={(e) => handlePriceChange(e, "discounted")}
                    onFocus={() =>
                      setIsFocused((prev) => ({ ...prev, discounted: true }))
                    }
                    onBlur={() =>
                      setIsFocused((prev) => ({ ...prev, discounted: false }))
                    }
                    pattern="^\d*\.?\d{0,2}$"
                    inputMode="decimal"
                    placeholder="0.00"
                    disabled={Number(originalPrice) <= 0}
                    className="py-4 pr-16 pl-12 w-full text-lg bg-transparent rounded-2xl outline-none"
                  />
                ) : (
                  <input
                    type="text"
                    value={discountPercentage}
                    onChange={handlePercentageChange}
                    onFocus={() =>
                      setIsFocused((prev) => ({ ...prev, discounted: true }))
                    }
                    onBlur={() =>
                      setIsFocused((prev) => ({ ...prev, discounted: false }))
                    }
                    pattern="^\d*\.?\d{0,1}$"
                    inputMode="decimal"
                    placeholder="0.0"
                    disabled={Number(originalPrice) <= 0}
                    className="py-4 pr-16 pl-12 w-full text-lg bg-transparent rounded-2xl outline-none"
                  />
                )}

                <motion.div
                  className="absolute right-4 top-1/2 text-xs text-gray-400 -translate-y-1/2"
                  animate={{ opacity: isFocused.discounted ? 1 : 0.5 }}
                >
                  {inputMode === "sales" ? ".00" : ".0"}
                </motion.div>
              </div>
              <motion.label
                className={`absolute -top-2.5 left-4 px-2 text-sm bg-white
                ${isFocused.discounted ? "text-[#FF6C6C]" : "text-gray-500"}`}
              >
                {inputMode === "sales" ? "Sales Price" : "Discount %"}
              </motion.label>
            </motion.div>
          </div>

          {/* Error Message */}
          <AnimatePresence>
            {priceError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-3 text-sm text-red-500 bg-red-50 rounded-xl border border-red-100"
              >
                {priceError}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Smart Validation Messages */}
          <AnimatePresence>
            {!priceError && originalPrice && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-3 text-sm text-green-600 bg-green-50 rounded-xl border border-green-100"
              >
                <div className="flex gap-2 items-center">
                  <span className="text-green-500">✓</span>
                  <span>
                    {inputMode === "sales"
                      ? `Valid pricing! ${discount.percentage}% discount applied.`
                      : `Valid discount! Final price: GHC ${discountedPrice}`}
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Discount Information */}
          <AnimatePresence>
            {originalPrice && discountedPrice && !priceError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="p-6 bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl border border-pink-100"
              >
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    className="flex flex-col sm:flex-row items-center p-2 sm:p-4 rounded-xl backdrop-blur-sm bg-white/50"
                  >
                    <div className="p-2 sm:p-3 bg-[#FF6C6C]/10 rounded-lg">
                      <Percent className="w-4 h-4 sm:w-6 sm:h-6 text-[#FF6C6C]" />
                    </div>
                    <div className="sm:ml-4 text-center sm:text-left mt-2 sm:mt-0">
                      <p className="text-xs sm:text-sm text-gray-500">Discount</p>
                      <p className="text-sm sm:text-lg font-bold text-[#FF6C6C] break-words">
                        {discount.percentage}%
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    className="flex flex-col sm:flex-row items-center p-2 sm:p-4 rounded-xl backdrop-blur-sm bg-white/50"
                  >
                    <div className="p-2 sm:p-3 bg-[#FF6C6C]/10 rounded-lg">
                      <Coins className="w-4 h-4 sm:w-6 sm:h-6 text-[#FF6C6C]" />
                    </div>
                    <div className="sm:ml-4 text-center sm:text-left mt-2 sm:mt-0">
                      <p className="text-xs sm:text-sm text-gray-500">Savings</p>
                      <p className="text-sm sm:text-lg font-bold text-[#FF6C6C] whitespace-nowrap">
                      ₵{discount.value}
                      </p>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    className="flex flex-col sm:flex-row items-center p-2 sm:p-4 rounded-xl backdrop-blur-sm bg-white/50"
                  >
                    <div className="p-2 sm:p-3 bg-[#FF6C6C]/10 rounded-lg">
                      <CircleDollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-[#FF6C6C]" />
                    </div>
                    <div className="sm:ml-4 text-center sm:text-left mt-2 sm:mt-0">
                      <p className="text-xs sm:text-sm text-gray-500">Final Price</p>
                      <p className="text-sm sm:text-lg font-bold text-[#FF6C6C] whitespace-nowrap">
                      ₵{discountedPrice}
                      </p>
                    </div>
                  </motion.div>
                </div>

                {/* Mode Switch Indicator */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="pt-4 mt-4 border-t border-pink-200"
                >
                  <div className="flex gap-2 justify-center items-center text-sm text-pink-700">
                    <ArrowRightLeft className="w-4 h-4" />
                    <span>
                      {inputMode === "sales"
                        ? `Entered sales price: GHC ${discountedPrice} (${discount.percentage}% off)`
                        : `Entered discount: ${discountPercentage}% (Final price: GHC ${discountedPrice})`}
                    </span>
                  </div>

                  {/* Quick Mode Switch Hint */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mt-2 text-center"
                  >
                    <div className="inline-flex gap-1 items-center px-3 py-1 text-xs text-pink-600 bg-pink-50 rounded-full">
                      <span>💡</span>
                      <span>
                        {inputMode === "sales"
                          ? "Switch to percentage mode to set discount directly"
                          : "Switch to sales price mode to set final price directly"}
                      </span>
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex flex-wrap gap-2 items-center mt-2 text-sm text-gray-500">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-2 items-center p-2 bg-gray-50 rounded-lg"
            >
              <span className="font-medium">✓</span>
              <span>Valid: 10.00, 10.50, 10.99</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-2 items-center p-2 text-red-500 bg-red-50 rounded-lg"
            >
              <span className="font-medium">✗</span>
              <span>Invalid: 10.0, 10.999, 10</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex gap-2 items-center p-2 text-blue-700 bg-blue-50 rounded-lg"
            >
              <span className="font-medium">💡</span>
              <span>Percentage: 0.0 to 100.0</span>
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }
);

export default PricingSection;
